import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { WALLPAPER_URLS } from '../data/wallpapers';
import { useRotatingWallpaper } from '../hooks/useRotatingWallpaper';

// `prefers-reduced-motion: reduce` のユーザーはローテーションを止めて静止表示にする。
// matchMedia が無い環境 (SSR / 古いブラウザ) では false 扱い。
const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const FADE_MS = 1_500;
const ROTATE_MS = 10_000;
// 暗オーバーレイの濃さ。前景の入力フォームの可読性を確保。
const OVERLAY_RGBA = 'rgba(0, 0, 0, 0.55)';
// 最初の壁紙が load し終わるまで（および全 URL 失効時の）グラデ背景。
const GRADIENT = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)';

const imgStyle = (visible: boolean, fadeMs: number): React.CSSProperties => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  opacity: visible ? 1 : 0,
  transition: `opacity ${fadeMs}ms ease-in-out`,
  pointerEvents: 'none',
});

export const WallpaperBackground: React.FC = () => {
  // インスタンス生成時に prefers-reduced-motion を一度だけ評価する。
  // タブ切替などでの動的変化は本対応スコープ外。
  const reduced = useMemo(prefersReducedMotion, []);
  const interval = reduced ? Number.MAX_SAFE_INTEGER : ROTATE_MS;
  const fade = reduced ? 0 : FADE_MS;

  const { urlA, urlB, showA } = useRotatingWallpaper(WALLPAPER_URLS, interval);

  return (
    <Box
      component="div"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        background: GRADIENT,
        overflow: 'hidden',
      }}
      aria-hidden
    >
      {urlA && (
        <img
          src={urlA}
          alt=""
          loading="eager"
          decoding="async"
          style={imgStyle(showA, fade)}
        />
      )}
      {urlB && (
        <img
          src={urlB}
          alt=""
          loading="eager"
          decoding="async"
          style={imgStyle(!showA, fade)}
        />
      )}
      <Box
        component="div"
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: OVERLAY_RGBA,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};
